import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { Button } from "../../../components/ui/button";
import { Card } from "../../../components/ui/card";
import { Skeleton } from "../../../components/ui/skeleton";
import { selectBasketItems } from "../../../store/selectors/basket";
import { formatPrice } from "../../../utils/getPrice";
import { impactOccuer } from "../../../utils/hapticEvents";
import { useAddToBasket } from "../services/addToBasket";
import {
  buildCartKey,
  buildModifierPayload,
  getChoosableGroups,
  getDefaultSelections,
  getModifierTotal,
} from "../../home/libs/modifiers";
import { useGetCrossSellProducts } from "../services/getCrossSellProducts";
import { CrossSellProduct } from "../types/CrossSell";
import { ProductImage } from "../../../components/ProductImage";

const CrossSellImage = ({ product }: { product: CrossSellProduct }) => (
  <ProductImage
    src={product.menu_image}
    alt={product.title_uz || product.title}
    loading="lazy"
    className="h-28 w-full object-contain"
    placeholderClassName="p-4"
  />
);

export const BasketCrossSellCarousel = ({ branchId }: { branchId: number }) => {
  const basketItems = useSelector(selectBasketItems);
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { addToBasket, isPending: isAdding } = useAddToBasket();

  const productIds = useMemo(
    () =>
      Array.from(
        new Set(
          basketItems
            .map((item) => Number(item.id))
            .filter((id) => Number.isInteger(id) && id > 0),
        ),
      ).sort((a, b) => a - b),
    [basketItems],
  );

  const { products, isLoading, isError } = useGetCrossSellProducts(
    branchId,
    productIds,
  );

  if (productIds.length === 0 || isError || (!isLoading && products.length === 0)) {
    return null;
  }

  const language = i18n.resolvedLanguage ?? i18n.language;
  const getTitle = (product: CrossSellProduct) => {
    if (language.startsWith("ru")) return product.title || product.title_uz;
    if (language.startsWith("en")) {
      return product.title_en || product.title_uz || product.title;
    }
    return product.title_uz || product.title;
  };

  return (
    <section className="flex flex-col gap-3 pt-2" aria-busy={isLoading}>
      {!isLoading && (
        <h2 className="px-4 text-display-md text-text-primary">
          {language.startsWith("ru")
            ? "Возможно, вам понравится"
            : language.startsWith("en")
              ? "You may also like"
              : "Sizga ham yoqishi mumkin"}
        </h2>
      )}

      <div className="flex snap-x snap-mandatory gap-2 overflow-x-auto px-4 pb-2">
        {isLoading
          ? Array.from({ length: 3 }, (_, index) => (
              <Card
                key={index}
                className="min-w-[156px] w-[44vw] max-w-[190px] snap-start space-y-2 rounded-[20px] border-none p-2 shadow-none"
              >
                <Skeleton className="h-28 w-full rounded-2xl" />
                <Skeleton className="h-4 w-4/5" />
                <Skeleton className="h-4 w-2/3" />
                <Skeleton className="h-8 w-full rounded-full" />
              </Card>
            ))
          : products.map((product) => (
              <Card
                key={product.id}
                role="link"
                tabIndex={0}
                className="flex min-w-[156px] w-[44vw] max-w-[190px] snap-start cursor-pointer flex-col gap-2 rounded-[20px] border-none p-2 shadow-none"
                onClick={() => navigate(`/${product.id}`)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    navigate(`/${product.id}`);
                  }
                }}
              >
                <CrossSellImage product={product} />
                <div className="flex min-h-12 flex-col gap-1 px-0.5">
                  <p className="text-caption-sm-medium text-text-primary">
                    {getTitle(product)}
                  </p>
                  <p className="text-body-sm-semibold text-text-primary">
                    {formatPrice(Number(product.org_price) || 0)} {t("sum")}
                  </p>
                </div>
                <Button
                  disabled={isAdding}
                  className="mt-auto h-8 w-full rounded-full bg-fill-background text-body-sm-semibold text-text-primary shadow-none"
                  onClick={async (event) => {
                    event.stopPropagation();
                    impactOccuer("light");

                    // Внутрь ведём только если там есть что выбрать: у комбо
                    // состав зафиксирован, его кладём сразу.
                    if (getChoosableGroups(product.modifier_groups).length) {
                      navigate(`/${product.id}`);
                      return;
                    }

                    const selections = getDefaultSelections(
                      product.modifier_groups,
                    );
                    const modifiers = buildModifierPayload(
                      product.modifier_groups ?? [],
                      selections,
                    );

                    await addToBasket({
                      product,
                      cartKey: buildCartKey(product.id, selections),
                      unitPrice:
                        (Number(product.org_price) || 0) +
                        getModifierTotal(modifiers),
                      selectedModifiers: modifiers,
                    });
                  }}
                >
                  {t("addToCart")}
                </Button>
              </Card>
            ))}
      </div>
    </section>
  );
};
